/**
 * The session writes still in flight, per session, so a message waits for them: a pick shown
 * ahead of its write (an agent before the first turn, a model or tier mid-chat) must land before a
 * message posts into that session, or the turn runs on what the row held before.
 *
 * Module-level like the composer's pending picks: every surface posting into a session shares it.
 */
const inFlight = new Map<string, Set<Promise<boolean>>>();

/** The model a write in flight will pin, carried on a send as well in case the write is lost. */
const pendingModels = new Map<string, string>();

/** Track a write; its promise resolves once it lands and rejects if it is refused. */
export function trackSessionWrite(
  flowSessionId: string,
  write: Promise<unknown>,
  model?: string,
) {
  const settled = write.then(
    () => true,
    () => false,
  );
  const writes = inFlight.get(flowSessionId) ?? new Set();
  writes.add(settled);
  inFlight.set(flowSessionId, writes);
  if (model) pendingModels.set(flowSessionId, model);
  void settled.then(() => {
    writes.delete(settled);
    if (!writes.size) inFlight.delete(flowSessionId);
    if (model && pendingModels.get(flowSessionId) === model) {
      pendingModels.delete(flowSessionId);
    }
  });
}

export class SessionWriteRefusedError extends Error {
  constructor() {
    super("This chat's settings didn't save, so the message wasn't sent.");
  }
}

/** The session's writes in flight now: taken when a send starts, so none can settle unseen. */
export function sessionWritesInFlight(flowSessionId: string) {
  return [...(inFlight.get(flowSessionId) ?? [])];
}

/**
 * Resolves once every write taken (by default, every one in flight now) has settled; rejects if any
 * was refused, so the message is not posted on a state the server turned down (the composer keeps
 * its text).
 */
export async function settleSessionWrites(
  flowSessionId: string,
  writes = sessionWritesInFlight(flowSessionId),
) {
  if (!writes.length) return;
  const results = await Promise.all(writes);
  if (results.includes(false)) throw new SessionWriteRefusedError();
}

/** The model a write in flight will pin on the session, if one is. */
export function pendingSessionModel(flowSessionId: string | undefined) {
  return flowSessionId ? pendingModels.get(flowSessionId) : undefined;
}

/** Test seam: the registry outlives component unmounts. */
export function clearSessionWrites() {
  inFlight.clear();
  pendingModels.clear();
}
